import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { api } from "../api";
import SEO from "../components/SEO";

const greetings = [
  { word: "Hello",          lang: "English" },
  { word: "Molo",           lang: "isiXhosa" },
  { word: "Sawubona",       lang: "isiZulu" },
  { word: "Dumela",         lang: "Sesotho" },
  { word: "Sanibonani",     lang: "isiZulu (plural)" },
  { word: "Avuxeni",        lang: "Xitsonga" },
  { word: "Lotjhani",       lang: "isiNdebele" },
  { word: "Hallo",          lang: "Afrikaans" },
  { word: "Ndi matsheloni", lang: "Tshivenda" },
];

const ctaButtons = [
  { label: "Plan a Health Campaign",   to: "/contact", style: "btn-red" },
  { label: "Schedule a Call",          to: "/contact", style: "btn-outline-white" },
  { label: "Sponsor a Podcast Series", to: "/contact", style: "btn-outline-white" },
  { label: "Collaborate With Us",      to: "/contact", style: "btn-outline-white" },
];

const services = [
  { icon: "🎯", title: "Health Campaign Strategy",      desc: "Planning, messaging, campaign concepts, audience mapping and creative direction." },
  { icon: "🎬", title: "Multimedia Content Creation",   desc: "Videos, photos, graphics, reels, scripts, social media and digital storytelling." },
  { icon: "📰", title: "PR & Media Relations",          desc: "Press releases, media pitching, interview coordination and campaign visibility." },
  { icon: "🎙", title: "Podcast & Youth Health Content", desc: "Sponsored series, youth conversations, expert interviews and myth-busting." },
  { icon: "🌍", title: "Community Health Activations",  desc: "Live events, school-based content, youth engagement and campaign documentation." },
  { icon: "🗣", title: "Multilingual Health Comms",     desc: "Translation, culturally relevant messaging for all South African communities." },
];

// ── Newsletter signup (self-contained) ───────────────────────
function NewsletterSignup() {
  const [name,    setName]    = useState("");
  const [email,   setEmail]   = useState("");
  const [status,  setStatus]  = useState("idle"); // idle | loading | success | error
  const [errMsg,  setErrMsg]  = useState("");

  const submit = async () => {
    if (!email.trim()) {
      setStatus("error");
      setErrMsg("Please enter your email address.");
      return;
    }
    setStatus("loading");
    try {
      await api.subscribe({ name: name.trim() || undefined, email: email.trim() });
      setStatus("success");
      setName("");
      setEmail("");
    } catch (e) {
      setStatus("error");
      setErrMsg(e.message || "Something went wrong. Please try again.");
    }
  };

  const handleKey = (e) => { if (e.key === "Enter") submit(); };

  return (
    <section style={{ background: "#007965", padding: "5rem 2rem" }}>
      <div className="container-sm" style={{ textAlign: "center" }}>
        <p className="section-label" style={{ color: "rgba(255,255,255,0.6)" }}>
          Grow with us
        </p>
        <h2 className="section-title" style={{ color: "#fff", marginBottom: "1rem" }}>
          Get youth health stories, campaign updates and podcast alerts.
        </h2>

        {status === "success" ? (
          <div style={{
            marginTop: "2rem",
            background: "rgba(255,255,255,0.12)",
            borderRadius: 14,
            padding: "1.5rem 2rem",
            display: "inline-block",
          }}>
            <p style={{ fontSize: "1.75rem", marginBottom: "0.5rem" }}>🎉</p>
            <p style={{
              fontFamily: "var(--font)", fontWeight: 700,
              color: "#fff", fontSize: "1.05rem", margin: 0,
            }}>
              You're in the Plug!
            </p>
            <p style={{
              color: "rgba(255,255,255,0.7)", fontSize: "0.88rem",
              marginTop: "0.4rem", fontFamily: "var(--font-body)",
            }}>
              Check your inbox — a welcome email is on its way.
            </p>
          </div>
        ) : (
          <>
            <div style={{
              display: "flex", gap: "0.75rem",
              maxWidth: 500, margin: "2rem auto 0",
              flexWrap: "wrap",
            }}>
              <input
                className="form-input newsletter-input"
                placeholder="Your name"
                value={name}
                onChange={e => setName(e.target.value)}
                onKeyDown={handleKey}
                disabled={status === "loading"}
                style={{ flex: 1, minWidth: 130, color: "#fff", background: "rgba(255,255,255,0.15)", border: "1px solid rgba(255,255,255,0.25)" }}
              />
              <input
                className="form-input newsletter-input"
                placeholder="Your email *"
                type="email"
                value={email}
                onChange={e => {
                  setEmail(e.target.value);
                  if (status === "error") { setStatus("idle"); setErrMsg(""); }
                }}
                onKeyDown={handleKey}
                disabled={status === "loading"}
                style={{ flex: 1, minWidth: 180, color: "#fff", background: "rgba(255,255,255,0.15)", border: "1px solid rgba(255,255,255,0.25)" }}
              />
              <button
                className="btn-red"
                onClick={submit}
                disabled={status === "loading"}
                style={{ whiteSpace: "nowrap", opacity: status === "loading" ? 0.7 : 1 }}
              >
                {status === "loading" ? "Joining…" : "Plug In"}
              </button>
            </div>

            {status === "error" && (
              <p style={{
                color: "rgba(255,210,210,0.95)", fontSize: "0.85rem",
                marginTop: "0.75rem", fontFamily: "var(--font-body)",
              }}>
                {errMsg}
              </p>
            )}
          </>
        )}
      </div>
    </section>
  );
}

// ── Home page ─────────────────────────────────────────────────
export default function Home() {
  const [featured, setFeatured] = useState([]);

  useEffect(() => {
    api.getCampaigns({ featured: true }).then(setFeatured).catch(() => {});
  }, []);

  return (
    <>
    <SEO
      path="/"
      description="The Health Plug is South Africa's youth-focused health PR and impact communications agency. We create multilingual, multimedia health campaigns on HIV, TB, mental health, sexual health and more."
      schema={{
        "@context": "https://schema.org",
        "@type": "WebSite",
        "name": "The Health Plug",
        "url": "https://thehealthplug.co.za",
        "potentialAction": {
          "@type": "SearchAction",
          "target": "https://thehealthplug.co.za/health-archive?q={search_term_string}",
          "query-input": "required name=search_term_string"
        }
      }}
    />
    <div style={{ background: "#0B0B0B" }}>

      {/* ── HERO ── */}
      <section style={{ minHeight: "100vh", position: "relative", overflow: "hidden", display: "flex", flexDirection: "column", justifyContent: "center", padding: "8rem 2rem 4rem" }}>

        {/* Background greeting words */}
        <div style={{ position: "absolute", inset: 0, overflow: "hidden", pointerEvents: "none", zIndex: 0 }}>
          {greetings.map((g, i) => (
            <span key={i} style={{
              position: "absolute",
              fontFamily: "var(--font)", fontWeight: 900,
              fontSize: `clamp(2.5rem, ${4 + (i % 3)}vw, 6rem)`,
              color: "transparent",
              WebkitTextStroke: "1px rgba(149,187,131,0.18)",
              whiteSpace: "nowrap",
              top: `${8 + (i * 10) % 85}%`,
              left: `${(i * 23) % 80}%`,
              transform: `rotate(${(i % 2 === 0 ? -1 : 1) * (5 + i % 8)}deg)`,
              filter: "blur(0.5px)",
              userSelect: "none",
            }}>{g.word}</span>
          ))}
          <span style={{ position: "absolute", bottom: "12%", right: "8%", fontSize: "5rem", opacity: 0.08 }}>🤟</span>
          <div style={{ position: "absolute", top: "30%", left: "50%", transform: "translate(-50%,-50%)", width: 600, height: 600, borderRadius: "50%", background: "radial-gradient(circle, rgba(138,22,19,0.15) 0%, transparent 70%)", pointerEvents: "none" }} />
        </div>

        <div style={{ maxWidth: 900, margin: "0 auto", width: "100%", position: "relative", zIndex: 1 }}>
          <div style={{ display: "inline-flex", alignItems: "center", gap: "0.5rem", background: "rgba(149,187,131,0.12)", border: "1px solid rgba(149,187,131,0.3)", borderRadius: 100, padding: "0.35rem 1rem", marginBottom: "2rem" }}>
            <span style={{ width: 7, height: 7, borderRadius: "50%", background: "#95BB83", display: "inline-block", animation: "pulse 2s infinite" }} />
            <span style={{ color: "#95BB83", fontFamily: "var(--font)", fontWeight: 600, fontSize: "0.75rem", letterSpacing: "0.12em", textTransform: "uppercase" }}>Youth Health PR &amp; Impact Communications</span>
          </div>

          <h1 style={{ fontFamily: "var(--font)", fontSize: "clamp(3.5rem, 9vw, 8rem)", fontWeight: 900, lineHeight: 0.95, letterSpacing: "-0.03em", color: "#fff", marginBottom: "1.5rem" }}>
            THE<br />
            <span style={{ color: "#8A1613" }}>HEALTH</span><br />
            PLUG
          </h1>

          <p style={{ color: "rgba(255,255,255,0.6)", fontSize: "clamp(0.95rem, 2vw, 1.15rem)", maxWidth: 600, lineHeight: 1.75, marginBottom: "2.5rem", fontFamily: "var(--font-body)", fontWeight: 300 }}>
            A youth-focused PR and Impact Communications agency creating multilingual, multimedia health content for young people, communities and organisations. We turn health information into stories, campaigns and conversations people can understand, trust and relate to.
          </p>

          <div style={{ display: "flex", gap: "0.75rem", flexWrap: "wrap" }}>
            {ctaButtons.map((b, i) => (
              <Link key={i} to={b.to} className={b.style} style={{ fontSize: "0.85rem", padding: "0.75rem 1.5rem" }}>{b.label}</Link>
            ))}
          </div>
        </div>
      </section>

      {/* ── SERVICES SNAPSHOT ── */}
      <section style={{ background: "#F7F1DF", padding: "6rem 2rem" }}>
        <div className="container">
          <p className="section-label" style={{ color: "#8A1613" }}>What We Do</p>
          <h2 className="section-title" style={{ color: "#0B0B0B", marginBottom: "0.75rem" }}>Health comms that<br /><span style={{ color: "#8A1613" }}>actually land.</span></h2>
          <p style={{ color: "rgba(11,11,11,0.55)", fontFamily: "var(--font-body)", marginBottom: "3rem", maxWidth: 520 }}>We help organisations communicate health information in ways people can understand, share and act on.</p>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "1.25rem" }} className="mobile-full">
            {services.map((s, i) => (
              <div key={i}
                style={{ background: "#fff", borderRadius: 16, padding: "2rem", border: "1px solid rgba(11,11,11,0.06)", transition: "box-shadow 0.2s, transform 0.2s" }}
                onMouseEnter={e => { e.currentTarget.style.boxShadow = "0 8px 32px rgba(138,22,19,0.12)"; e.currentTarget.style.transform = "translateY(-3px)"; }}
                onMouseLeave={e => { e.currentTarget.style.boxShadow = "none"; e.currentTarget.style.transform = "translateY(0)"; }}
              >
                <div style={{ fontSize: "2rem", marginBottom: "0.75rem" }}>{s.icon}</div>
                <h3 style={{ fontFamily: "var(--font)", fontWeight: 700, fontSize: "1rem", marginBottom: "0.5rem", color: "#0B0B0B" }}>{s.title}</h3>
                <p style={{ color: "rgba(11,11,11,0.5)", fontSize: "0.85rem", lineHeight: 1.65, fontFamily: "var(--font-body)" }}>{s.desc}</p>
              </div>
            ))}
          </div>

        </div>
      </section>

      {/* ── PODCAST FEATURE ── */}
      <section style={{ background: "#0B0B0B", padding: "6rem 2rem", position: "relative", overflow: "hidden" }}>
        <div style={{ position: "absolute", inset: 0, pointerEvents: "none", opacity: 0.04 }}>
          {["PODCAST","HEALTH","STORIES","YOUTH","VOICES"].map((w, i) => (
            <span key={i} style={{ position: "absolute", fontFamily: "var(--font)", fontWeight: 900, fontSize: "8rem", color: "#95BB83", top: `${i*22}%`, left: `${i*18}%`, whiteSpace: "nowrap" }}>{w}</span>
          ))}
        </div>
        <div className="container" style={{ position: "relative", zIndex: 1 }}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "4rem", alignItems: "center" }} className="mobile-full">
            <div>
              <p className="section-label" style={{ color: "#95BB83" }}>Now Playing</p>
              <h2 className="section-title" style={{ color: "#fff", marginBottom: "1.25rem" }}>
                The Health Plug<br /><span style={{ color: "#8A1613" }}>Podcast</span>
              </h2>
              <p style={{ color: "rgba(255,255,255,0.55)", lineHeight: 1.8, marginBottom: "2rem", fontFamily: "var(--font-body)" }}>
                A community-based health edutainment show where young people share lived experiences, ask questions openly, hear from experts and engage with health news and real stories. A safe, energetic and accessible space.
              </p>
              <div style={{ display: "flex", gap: "0.75rem", flexWrap: "wrap" }}>
                <Link to="/contact" className="btn-red">Sponsor a Series</Link>
              </div>
            </div>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
              <div style={{ width: 260, height: 260, borderRadius: "50%", background: "linear-gradient(135deg, #8A1613 0%, #95BB83 100%)", display: "flex", alignItems: "center", justifyContent: "center", position: "relative", boxShadow: "0 0 80px rgba(138,22,19,0.3)" }}>
                <span style={{ fontSize: "5rem" }}>🎙</span>
                {[1,2,3].map(r => (
                  <div key={r} style={{ position: "absolute", inset: `-${r*20}px`, borderRadius: "50%", border: "1px solid rgba(149,187,131,0.15)" }} />
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── FEATURED WORK ── */}
      {featured.length > 0 && (
        <section style={{ background: "#F7F1DF", padding: "6rem 2rem" }}>
          <div className="container">
            <p className="section-label" style={{ color: "#8A1613" }}>Featured Work</p>
            <h2 className="section-title" style={{ color: "#0B0B0B", marginBottom: "0.75rem" }}>Campaign stories</h2>
            <p style={{ color: "rgba(11,11,11,0.5)", fontFamily: "var(--font-body)", marginBottom: "3rem" }}>A look at health communication projects created with communities and partners.</p>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "1.25rem" }} className="mobile-full">
              {featured.slice(0, 3).map(c => <CampaignCard key={c.id} c={c} />)}
            </div>
            <div style={{ textAlign: "center", marginTop: "2.5rem" }}>
              <Link to="/clients" className="btn-outline-red">View More</Link>
            </div>
          </div>
        </section>
      )}

      {/* ── NEWSLETTER ── */}
      <NewsletterSignup />

      {/* ── PARTNERS ── */}
      <section style={{ background: "#0B0B0B", padding: "4rem 2rem" }}>
        <div className="container">
          <p style={{ textAlign: "center", fontFamily: "var(--font)", fontWeight: 700, fontSize: "0.75rem", letterSpacing: "0.15em", textTransform: "uppercase", color: "rgba(255,255,255,0.3)", marginBottom: "2rem" }}>Our Community of Support</p>
          <div style={{ display: "flex", justifyContent: "center", gap: "3rem", flexWrap: "wrap", alignItems: "center" }}>
            {["Fraymedia Foundation", "Thetha Ndoda Foundation", "TBPROOF"].map(p => (
              <div key={p} style={{ background: "rgba(231, 240, 240, 0.05)", borderRadius: 12, padding: "1rem 2rem", color: "rgba(255, 255, 255, 0.98)", fontFamily: "var(--font)", fontWeight: 600, fontSize: "0.9rem", border: "1px solid rgba(255,255,255,0.07)" }}>{p}</div>
            ))}
          </div>
        </div>
      </section>

      <style>{`
        @keyframes pulse { 0%,100%{opacity:1} 50%{opacity:0.4} }
        .newsletter-input::placeholder { color: rgba(255,255,255,0.6) !important; }
      `}</style>
    </div>
    </>
  );
}

// ── Campaign card (homepage only) ────────────────────────────
function CampaignCard({ c }) {
  return (
    <Link to="/clients" className="card" style={{ background: "#fff", border: "1px solid rgba(11,11,11,0.07)", display: "block" }}>
      <div style={{ height: 140, background: `linear-gradient(135deg, ${c.color || "#8A1613"}, ${c.accent || "#95BB83"})`, display: "flex", alignItems: "center", justifyContent: "center", position: "relative" }}>
        {c.image_url
          ? <img src={c.image_url} alt={c.title} style={{ width: "100%", height: "100%", objectFit: "cover", position: "absolute", inset: 0 }} />
          : <span style={{ fontFamily: "var(--font)", fontSize: "3rem", fontWeight: 900, color: "rgba(255,255,255,0.18)" }}>{c.tag}</span>
        }
      </div>
      <div style={{ padding: "1.25rem" }}>
        <p style={{ fontSize: "0.7rem", color: "#8A1613", fontFamily: "var(--font)", fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: "0.3rem" }}>{c.category}</p>
        <h3 style={{ fontFamily: "var(--font)", fontWeight: 700, fontSize: "1rem", color: "#0B0B0B", marginBottom: "0.4rem" }}>{c.title}</h3>
        <p style={{ fontSize: "0.82rem", color: "rgba(11,11,11,0.45)", fontFamily: "var(--font-body)" }}>{c.client}</p>
      </div>
    </Link>
  );
}
